/**
 * MODULE: domain/pages/frontend/hooks/usePages.ts
 * PURPOSE: TanStack Query hooks for pages API
 * DEPENDENCIES: pagesApi, ../utils/workspaceId (resolvePagesWorkspaceId)
 * PUBLIC_API: usePageTree, useCreatePage, PAGE_KEYS, …
 * SIDE_EFFECTS: HTTP via pagesApi
 * AGENT SAFETY: 🟢 SAFE
 * RECENT: 2026-05-24 [2.2.1139] — revision restore invalidates database schema/rows/views queries. [2.2.1137] — gate workspace queries until workspace_id resolves to UUID.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { pagesApi } from '../api/pagesApi';
import { getPageBrainActivity } from '../api/pageBrainActivityApi';
import {
  pageBrainActivityScopeFromPage,
  pageBrainActivityScopeKey,
  type PageBrainActivityScope,
} from '../utils/pageBrainActivityScope';
import { applyPageMoveToTree, type PageMoveUpdate } from '../utils/pageTreeUtils';
import { resolvePagesWorkspaceId } from '../utils/workspaceId';
import {
  CRM_BRAIN_QUERY_KEYS,
  refreshPageEngagementBrainQueriesByPageId,
} from '../utils/crmBrainQueryUtils';
import type {
  CreatePageRequest,
  CreatePageCommentRequest,
  Page,
  CreatePageFromTemplateRequest,
  CreatePageViewRequest,
  DuplicatePageRequest,
  FlatNotesMigrationRequest,
  GrantPagePermissionRequest,
  MoveBlockRequest,
  PageStatus,
  PageTreeNode,
  SavePageAsTemplateRequest,
  UpdateDatabaseRowRequest,
  UpdateDatabaseSchemaRequest,
  UpdatePageRequest,
} from '../../contracts/pages.types';

export const PAGE_KEYS = {
  tree: (wsId: string) => ['pages', 'tree', wsId] as const,
  archived: (wsId: string) => ['pages', 'archived', wsId] as const,
  search: (wsId: string, query: string) => ['pages', 'search', wsId, query] as const,
  detail: (pageId: string) => ['pages', pageId] as const,
  permissions: (pageId: string) => ['pages', pageId, 'permissions'] as const,
  effectivePermission: (pageId: string) => ['pages', pageId, 'permissions', 'me'] as const,
  comments: (pageId: string) => ['pages', pageId, 'comments'] as const,
  commentNotificationPrefs: () => ['pages', 'comment-notification-prefs'] as const,
  backlinks: (pageId: string) => ['pages', pageId, 'backlinks'] as const,
  revisions: (pageId: string) => ['pages', pageId, 'revisions'] as const,
  brainActivity: (pageId: string, scopeKey = '', eventIdsKey = '') =>
    ['pages', pageId, 'brain-activity', scopeKey, eventIdsKey] as const,
  revision: (pageId: string, revisionId: string) =>
    ['pages', pageId, 'revisions', revisionId] as const,
  dealRoom: (pageId: string) => ['pages', pageId, 'deal-room'] as const,
  crmContext: (pageId: string) => ['pages', pageId, 'crm-context'] as const,
  byDeal: (dealId: string, workspaceId?: string) =>
    ['pages', 'by-deal', dealId, workspaceId ?? 'all'] as const,
  byCompany: (companyId: string, workspaceId?: string) =>
    ['pages', 'by-company', companyId, workspaceId ?? 'all'] as const,
  byContact: (contactId: string, workspaceId?: string) =>
    ['pages', 'by-contact', contactId, workspaceId ?? 'all'] as const,
  blocks: (pageId: string) => ['pages', pageId, 'blocks'] as const,
  schema: (pageId: string) => ['pages', pageId, 'schema'] as const,
  rows: (pageId: string) => ['pages', pageId, 'rows'] as const,
  views: (pageId: string) => ['pages', pageId, 'views'] as const,
  migrationStatus: (workspaceId: string) => ['pages', 'migration-status', workspaceId] as const,
  templates: (workspaceId?: string) => ['pages', 'templates', workspaceId ?? 'all'] as const,
  favorites: (wsId: string) => ['pages', 'favorites', wsId] as const,
  shareStatus: (pageId: string) => ['pages', pageId, 'share'] as const,
};

function pagesWorkspaceQueriesEnabled(workspaceId: string): boolean {
  return resolvePagesWorkspaceId(workspaceId) !== null;
}

export function usePageTree(workspaceId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.tree(workspaceId),
    queryFn: () => pagesApi.getTree(workspaceId),
    enabled: pagesWorkspaceQueriesEnabled(workspaceId),
  });
}

export function useArchivedPages(workspaceId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.archived(workspaceId),
    queryFn: () => pagesApi.getArchivedPages(workspaceId),
    enabled: pagesWorkspaceQueriesEnabled(workspaceId),
  });
}

export function usePageSearch(workspaceId: string, query: string) {
  const normalized = query.trim();
  return useQuery({
    queryKey: PAGE_KEYS.search(workspaceId, normalized),
    queryFn: () => pagesApi.searchPages(workspaceId, normalized),
    enabled: pagesWorkspaceQueriesEnabled(workspaceId) && normalized.length >= 2,
    staleTime: 30_000,
  });
}

export function usePage(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.detail(pageId),
    queryFn: () => pagesApi.getPage(pageId),
    enabled: Boolean(pageId),
  });
}

export function usePageEffectivePermission(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.effectivePermission(pageId),
    queryFn: () => pagesApi.getMyPagePermission(pageId),
    enabled: Boolean(pageId),
  });
}

export function usePagePermissions(pageId: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.permissions(pageId),
    queryFn: () => pagesApi.getPagePermissions(pageId),
    enabled: Boolean(pageId) && enabled,
  });
}

export function usePageShareStatus(pageId: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.shareStatus(pageId),
    queryFn: () => pagesApi.getPageShareStatus(pageId),
    enabled: Boolean(pageId) && enabled,
    staleTime: 30_000,
  });
}

export function useGrantPagePermission(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: GrantPagePermissionRequest) => pagesApi.grantPagePermission(pageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.permissions(pageId) });
    },
  });
}

export function useRevokePagePermission(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (granteeUserId: string) => pagesApi.revokePagePermission(pageId, granteeUserId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.permissions(pageId) });
    },
  });
}

export function usePageComments(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.comments(pageId),
    queryFn: () => pagesApi.getPageComments(pageId),
    enabled: Boolean(pageId),
  });
}

export function usePageBacklinks(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.backlinks(pageId),
    queryFn: () => pagesApi.getPageBacklinks(pageId),
    enabled: Boolean(pageId),
    staleTime: 60_000,
  });
}

export function usePageRevisions(pageId: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.revisions(pageId),
    queryFn: () => pagesApi.getPageRevisions(pageId),
    enabled: Boolean(pageId) && enabled,
  });
}

export type UsePageBrainActivityOptions = {
  /** @deprecated Prefer crmScope or pass scope via page in PageActivityPanel */
  linkedDealId?: string | null;
  linkedContactId?: string | null;
  linkedCompanyId?: string | null;
  crmScope?: PageBrainActivityScope;
  /** When set, fetches specific activity_events ids (insight View signals). */
  signalEventIds?: string[];
  enabled?: boolean;
};

export function usePageBrainActivity(
  pageId: string,
  options: UsePageBrainActivityOptions = {},
) {
  const {
    linkedDealId,
    linkedContactId,
    linkedCompanyId,
    crmScope,
    signalEventIds = [],
    enabled = true,
  } = options;
  const scope: PageBrainActivityScope = crmScope ?? {
    dealId: (linkedDealId || '').trim() || undefined,
    contactId: (linkedContactId || '').trim() || undefined,
    companyId: (linkedCompanyId || '').trim() || undefined,
  };
  const scopeKey = pageBrainActivityScopeKey(scope);
  const eventIds = signalEventIds.map((id) => id.trim()).filter(Boolean);
  const eventIdsKey = [...eventIds].sort().join(',');
  return useQuery({
    queryKey: PAGE_KEYS.brainActivity(pageId, scopeKey, eventIdsKey),
    queryFn: () => getPageBrainActivity(pageId, 25, scope, eventIds),
    enabled: Boolean(pageId) && enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

export { pageBrainActivityScopeFromPage, pageBrainActivityScopeKey };

export function usePageRevision(pageId: string, revisionId: string | null) {
  return useQuery({
    queryKey: PAGE_KEYS.revision(pageId, revisionId ?? ''),
    queryFn: () => pagesApi.getPageRevision(pageId, revisionId!),
    enabled: Boolean(pageId) && Boolean(revisionId),
  });
}

export function useSavePageRevision(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => pagesApi.savePageRevision(pageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.revisions(pageId) });
    },
  });
}

export function useRestorePageRevision(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revisionId: string) => pagesApi.restorePageRevision(pageId, revisionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.detail(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.blocks(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.schema(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.rows(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.views(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.revisions(pageId) });
      qc.invalidateQueries({ queryKey: ['pages', 'tree'] });
    },
  });
}

export function usePageDealRoom(pageId: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.dealRoom(pageId),
    queryFn: () => pagesApi.getPageDealRoom(pageId),
    enabled: Boolean(pageId) && enabled,
    staleTime: 30_000,
  });
}

export function usePageCrmContext(pageId: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.crmContext(pageId),
    queryFn: () => pagesApi.getPageCrmContext(pageId),
    enabled: Boolean(pageId) && enabled,
    staleTime: 30_000,
  });
}

export function usePagesByDeal(dealId: string, workspaceId?: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.byDeal(dealId, workspaceId),
    queryFn: () => pagesApi.getPagesByDeal(dealId, workspaceId),
    enabled: Boolean(dealId) && enabled,
    staleTime: 30_000,
  });
}

export function usePagesByCompany(companyId: string, workspaceId?: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.byCompany(companyId, workspaceId),
    queryFn: () => pagesApi.getPagesByCompany(companyId, workspaceId),
    enabled: Boolean(companyId) && enabled,
    staleTime: 30_000,
  });
}

export function usePagesByContact(contactId: string, workspaceId?: string, enabled = true) {
  return useQuery({
    queryKey: PAGE_KEYS.byContact(contactId, workspaceId),
    queryFn: () => pagesApi.getPagesByContact(contactId, workspaceId),
    enabled: Boolean(contactId) && enabled,
    staleTime: 30_000,
  });
}

export function useCreatePageComment(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePageCommentRequest) => pagesApi.createPageComment(pageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.comments(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.detail(pageId) });
      const page = qc.getQueryData<Page>(PAGE_KEYS.detail(pageId));
      refreshPageEngagementBrainQueriesByPageId(qc, pageId, page ?? null);
    },
  });
}

export function useDeletePageComment(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => pagesApi.deletePageComment(pageId, commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.comments(pageId) });
    },
  });
}

export function useResolvePageComment(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, resolved }: { commentId: string; resolved: boolean }) =>
      pagesApi.resolvePageComment(pageId, commentId, resolved),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.comments(pageId) });
    },
  });
}

export function usePageCommentNotificationPrefs() {
  return useQuery({
    queryKey: PAGE_KEYS.commentNotificationPrefs(),
    queryFn: () => pagesApi.getPageCommentNotificationPreferences(),
    staleTime: 60_000,
  });
}

export function useUpdatePageCommentNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { mention_notifications_enabled: boolean }) =>
      pagesApi.updatePageCommentNotificationPreferences(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.commentNotificationPrefs() });
    },
  });
}

export function usePageBlocks(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.blocks(pageId),
    queryFn: () => pagesApi.getBlocks(pageId),
    enabled: Boolean(pageId),
  });
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePageRequest) => pagesApi.createPage(data),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(page.workspace_id) });
    },
  });
}

export function usePageTemplates(workspaceId?: string) {
  return useQuery({
    queryKey: PAGE_KEYS.templates(workspaceId),
    queryFn: () => pagesApi.getPageTemplates(workspaceId),
    enabled: workspaceId ? pagesWorkspaceQueriesEnabled(workspaceId) : true,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSavePageAsTemplate(pageId: string, workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SavePageAsTemplateRequest) => pagesApi.savePageAsTemplate(pageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.templates(workspaceId) });
    },
  });
}

export function useDeletePageTemplate(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => pagesApi.deletePageTemplate(templateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.templates(workspaceId) });
    },
  });
}

export function usePageFavorites(workspaceId: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: PAGE_KEYS.favorites(workspaceId),
    queryFn: () => pagesApi.getPageFavorites(workspaceId),
    enabled: pagesWorkspaceQueriesEnabled(workspaceId),
  });

  const mutation = useMutation({
    mutationFn: (pageIds: string[]) =>
      pagesApi.setPageFavorites(workspaceId, { page_ids: pageIds }),
    onMutate: async (pageIds) => {
      await qc.cancelQueries({ queryKey: PAGE_KEYS.favorites(workspaceId) });
      const previous = qc.getQueryData<{ page_ids: string[] }>(PAGE_KEYS.favorites(workspaceId));
      qc.setQueryData(PAGE_KEYS.favorites(workspaceId), { page_ids: pageIds });
      return { previous };
    },
    onError: (_error, _pageIds, context) => {
      if (context?.previous) {
        qc.setQueryData(PAGE_KEYS.favorites(workspaceId), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.favorites(workspaceId) });
    },
  });

  return {
    favoriteIds: query.data?.page_ids ?? [],
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    setFavoriteIds: mutation.mutateAsync,
    favoritesQuery: query,
  };
}

export function useCreatePageFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePageFromTemplateRequest) => pagesApi.createPageFromTemplate(data),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(page.workspace_id) });
      qc.invalidateQueries({ queryKey: ['pages', 'search', page.workspace_id] });
    },
  });
}

export function useUpdatePage(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdatePageRequest) => pagesApi.updatePage(pageId, data),
    onSuccess: (page, variables) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.detail(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.dealRoom(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.crmContext(pageId) });
      qc.invalidateQueries({ queryKey: ['pages', 'by-deal'] });
      qc.invalidateQueries({ queryKey: ['pages', 'by-company'] });
      qc.invalidateQueries({ queryKey: ['pages', 'by-contact'] });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(page.workspace_id) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.archived(page.workspace_id) });
      qc.invalidateQueries({ queryKey: ['pages', 'search', page.workspace_id] });
      if (Object.prototype.hasOwnProperty.call(variables, 'is_locked')) {
        refreshPageEngagementBrainQueriesByPageId(qc, pageId, page);
      }
    },
  });
}

export function useUpdatePageTitle(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pageId, title }: { pageId: string; title: string }) =>
      pagesApi.updatePage(pageId, { title }),
    onSuccess: (page, { pageId }) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.detail(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.dealRoom(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.crmContext(pageId) });
      qc.invalidateQueries({ queryKey: ['pages', 'by-deal'] });
      qc.invalidateQueries({ queryKey: ['pages', 'by-company'] });
      qc.invalidateQueries({ queryKey: ['pages', 'by-contact'] });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(workspaceId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.archived(workspaceId) });
      qc.invalidateQueries({ queryKey: ['pages', 'search', workspaceId] });
    },
  });
}

export function useSetPageStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pageId,
      status,
    }: {
      pageId: string;
      workspaceId: string;
      status: PageStatus;
    }) => pagesApi.updatePage(pageId, { status }),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.detail(page.id) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(page.workspace_id) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.archived(page.workspace_id) });
      qc.invalidateQueries({ queryKey: ['pages', 'search', page.workspace_id] });
    },
  });
}

export function useDuplicatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pageId, data }: { pageId: string; data?: DuplicatePageRequest }) =>
      pagesApi.duplicatePage(pageId, data),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(page.workspace_id) });
      qc.invalidateQueries({ queryKey: ['pages', 'search', page.workspace_id] });
    },
  });
}

export function useMovePageTree(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: PageMoveUpdate[]) => {
      await Promise.all(
        updates.map((update) =>
          pagesApi.updatePage(update.pageId, {
            parent_page_id: update.parent_page_id,
            position: update.position,
          })
        )
      );
    },
    onMutate: async (updates) => {
      await qc.cancelQueries({ queryKey: PAGE_KEYS.tree(workspaceId) });
      const previous = qc.getQueryData<PageTreeNode[]>(PAGE_KEYS.tree(workspaceId));
      if (previous) {
        qc.setQueryData(PAGE_KEYS.tree(workspaceId), applyPageMoveToTree(previous, updates));
      }
      return { previous };
    },
    onError: (_error, _updates, context) => {
      if (context?.previous) {
        qc.setQueryData(PAGE_KEYS.tree(workspaceId), context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(workspaceId) });
    },
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pageId }: { pageId: string; workspaceId: string }) => pagesApi.deletePage(pageId),
    onSuccess: (_, { workspaceId }) => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(workspaceId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.archived(workspaceId) });
    },
  });
}

export function refreshLinkedPageBrainAfterContentEdit(
  qc: QueryClient,
  pageId: string,
): void {
  const page = qc.getQueryData<Page>(PAGE_KEYS.detail(pageId));
  if (
    page &&
    (page.linked_company_id || page.linked_deal_id || page.linked_contact_id)
  ) {
    refreshPageEngagementBrainQueriesByPageId(qc, pageId, page);
    return;
  }
  void qc.invalidateQueries({ queryKey: CRM_BRAIN_QUERY_KEYS.prefix() });
}

export function useCreateBlock(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof pagesApi.createBlock>[1]) =>
      pagesApi.createBlock(pageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.blocks(pageId) });
      refreshLinkedPageBrainAfterContentEdit(qc, pageId);
    },
  });
}

export function useUpdateBlock(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, data }: { blockId: string; data: Parameters<typeof pagesApi.updateBlock>[1] }) =>
      pagesApi.updateBlock(blockId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.blocks(pageId) });
      refreshLinkedPageBrainAfterContentEdit(qc, pageId);
    },
  });
}

export function useDeleteBlock(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockId: string) => pagesApi.deleteBlock(blockId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.blocks(pageId) });
      refreshLinkedPageBrainAfterContentEdit(qc, pageId);
    },
  });
}

export function useMoveBlock(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, data }: { blockId: string; data: MoveBlockRequest }) =>
      pagesApi.moveBlock(blockId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.blocks(pageId) });
      refreshLinkedPageBrainAfterContentEdit(qc, pageId);
    },
  });
}

export function useDatabaseSchema(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.schema(pageId),
    queryFn: () => pagesApi.getDatabaseSchema(pageId),
    enabled: Boolean(pageId),
  });
}

export function useUpdateDatabaseSchema(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateDatabaseSchemaRequest) => pagesApi.updateDatabaseSchema(pageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.schema(pageId) });
    },
  });
}

export function useDatabaseRows(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.rows(pageId),
    queryFn: () => pagesApi.getRows(pageId),
    enabled: Boolean(pageId),
  });
}

export function useCreateDatabaseRow(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (properties?: Record<string, unknown>) =>
      pagesApi.createRow(pageId, { properties: properties ?? {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.rows(pageId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.detail(pageId) });
    },
  });
}

export function useUpdateDatabaseRow(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId, data }: { rowId: string; data: UpdateDatabaseRowRequest }) =>
      pagesApi.updateRow(pageId, rowId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.rows(pageId) });
    },
  });
}

export function useDeleteDatabaseRow(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rowId: string) => pagesApi.deleteRow(pageId, rowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.rows(pageId) });
    },
  });
}

export function usePageViews(pageId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.views(pageId),
    queryFn: () => pagesApi.getViews(pageId),
    enabled: Boolean(pageId),
  });
}

export function useCreatePageView(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePageViewRequest) => pagesApi.createView(pageId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.views(pageId) });
    },
  });
}

export function useUpdatePageView(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      viewId,
      data,
    }: {
      viewId: string;
      data: Partial<CreatePageViewRequest>;
    }) => pagesApi.updateView(pageId, viewId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.views(pageId) });
    },
  });
}

export function useFlatNotesMigrationStatus(workspaceId: string) {
  return useQuery({
    queryKey: PAGE_KEYS.migrationStatus(workspaceId),
    queryFn: () => pagesApi.getFlatNotesMigrationStatus(workspaceId),
    enabled: pagesWorkspaceQueriesEnabled(workspaceId),
    staleTime: 60_000,
  });
}

export function useMigrateFlatNotes(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FlatNotesMigrationRequest) =>
      pagesApi.migrateFlatNotes({ workspace_id: workspaceId, ...data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAGE_KEYS.migrationStatus(workspaceId) });
      qc.invalidateQueries({ queryKey: PAGE_KEYS.tree(workspaceId) });
      qc.invalidateQueries({ queryKey: ['pages', 'search', workspaceId] });
    },
  });
}
